import { useCallback, useEffect, useMemo, useState } from "react";
import { useShallow } from "zustand/react/shallow";

import type { CommandResult, Market, PositionsResponse, Token } from "../api/types";
import { usePositionsOf } from "../hooks/usePositions";
import { useStatus } from "../hooks/useStatus";
import { filledPositionOf, positionsFromFills, type FilledPosition } from "../lib/fills";
import { accountLabel, partialNote } from "../lib/accounts";
import { indexedOf, mergeSnapshots, scopeFills, sellPlan, type SellPlan } from "../lib/holdings";
import type { HotkeyAction } from "../lib/hotkeys";
import { add, format, parse, toFixed, type Micro } from "../lib/money";
import { SELL_FRACTIONS, journalAfterSale, positionOf, type SellFraction } from "../lib/position";
import { MAKER_PRICE, isPostOnlyCross, resolveMaker } from "../lib/maker";
import { checkAgainstMarket, coldSizes, matchesPresign } from "../lib/presets";
import { quickRows, rowAccounts, rowOfChoice, type QuickRow } from "../lib/quickrows";
import { warmRequest } from "../lib/warm";
import { fillsOfMarket, useFills } from "../store/useFills";
import {
  actionLabel,
  isNoMatch,
  referencePrice,
  resolveQuick,
  type QuickAction,
  type Resolved,
} from "../lib/quick";
import { useAutoMaker, useSetAutoMaker } from "../hooks/useAutoMaker";
import { useWarmSizes } from "../hooks/useWarmSizes";
import { commandTargetNow, useAccounts, useHasCommandTarget } from "../store/useAccounts";
import { useBooks } from "../store/useBooks";
import { armExpired, setHotkeySink, useHotkeys } from "../store/hotkeys";
import { cancelEverywhere, useOrders } from "../store/useOrders";
import { isTerminal } from "../store/orders";
import { activeSizeOf, useSettings } from "../store/useSettings";
import { isEngaged } from "./KillSwitch";
import { LatencyBadge } from "./LatencyBadge";
import { Panel } from "./Panel";
import { ErrorBoundary } from "./ErrorBoundary";
import { PFQuickRow } from "./PFQuickRow";
import { QuickTable, outcomeColor, type QuickCell, type QuickRowData } from "./QuickTable";
import { SizePresetsEditor } from "./SizePresetsEditor";
import { SubsystemAccountSelect } from "./SubsystemAccountSelect";

/**
 * Панель быстрых действий.
 *
 * # Строка — аккаунт или когорта, колонки — исходы
 *
 * С 2026-10-07 (замечание владельца) панель показывает ВСЕ аккаунты набора
 * сразу, сгруппированные по когортам, и у каждой строки свои кнопки: купить
 * мейкером и тейкером, закрыть выбранной долей, снять ордера. Цель нажатия —
 * строка, в которой нажали; переключать аккаунт перед ставкой больше не
 * нужно. Общий выбор цели остался у формы ордера (над ней, только для неё).
 *
 * Сама таблица — QuickTable; здесь — данные строк (позиции, планы закрытия),
 * отправка команд, эхо и клавиши.
 *
 * # Клавиши — от активной строки
 *
 * Q/W/A/S и ⇧ бьют от АКТИВНОЙ строки — той, по имени которой щёлкнули
 * (settings.target). От неё же берутся сигналы, считается тир и закрывается
 * пробная позиция. Геометрия клавиш прежняя: Q/W — верхний исход, A/S —
 * нижний, слева мейкер, справа тейкер.
 *
 * # Продажа
 *
 * Закрытие — по ноге на аккаунт строки (lib/holdings.sellPlan): у аккаунта
 * нога одна, у когорты — по одной на члена, и каждый продаёт долю СВОЕЙ
 * позиции со своего кошелька. Размер — из снимка кошелька, а когда тот пуст —
 * из журнала НАШИХ сделок этого аккаунта (живой стенд 2026-10-07: журнал,
 * сложенный по всем аккаунтам, продал DOWN A02 от имени A01).
 *
 * # Два правила, которые выглядят противоречиво, но таковыми не являются
 *
 *  1. Кнопка ВЫКЛЮЧАЕТСЯ, когда цену или размер взять неоткуда. Это не запрет,
 *     а отсутствие входных данных.
 *  2. Кнопка ОСТАЁТСЯ ЖИВОЙ при взведённом аварийном стопе и лишь предупреждает.
 *     Запрет — дело трейдера, и его мотивированный отказ оператор обязан
 *     увидеть; заблокированная кнопка молчит.
 */
interface Props {
  market: Market;
  /** Исходы В ПОРЯДКЕ outcome_index: порядок задаёт колонки и привязку клавиш. */
  tokens: Token[];
  /** Утилиты карточки: панель стоит в сетке и тянется до низа соседней колонки. */
  className?: string | undefined;
  /** Окно predict.fun в паре (ADR-0040): строка PF в таблице; null — её нет. */
  pfSlug?: string | null | undefined;
}

/** Сколько держать эхо последнего действия. */
const ECHO_MS = 6000;

interface Echo {
  text: string;
  tone: "ok" | "bad" | "muted";
}

/** Нажатие, вердикта которого ждёт эхо: по команде на аккаунт. */
interface Awaiting {
  label: string;
  legs: { traceId: string; account: string }[];
  /** Пробные ордера: вердикт им даёт стакан трейдера, а не биржа. */
  paper?: boolean;
}

/** Пустой список вердиктов — общий, чтобы useShallow не видел «перемен». */
const NO_VERDICTS: never[] = [];

/** Снимок кошелька аккаунта и почему его нет. */
interface AccountSnap {
  data: PositionsResponse | undefined;
  /** Почему позиция неизвестна; null — известна. */
  problem: string | null;
}

/**
 * Вердикт ОДНОЙ команды словами — хвост к подписи нажатия.
 *
 * Три исхода, и ни один из них не «ok» по умолчанию: приняли, отказали,
 * судьба неизвестна.
 */
function verdictEcho(res: CommandResult, paper = false): { text: string; tone: Echo["tone"] } {
  // «Принят биржей» над пробой — ложь о деньгах: оператор решит, что ордер
  // ушёл на биржу. Проба налита по стакану трейдера и лежит в журнале проб.
  if (res.accepted) return { text: paper ? " → проба налита по стакану" : " → принят биржей", tone: "ok" };
  if (res.unresolved) {
    // НЕ отказ: ордер мог быть принят, и его судьбу выясняет сверка трейдера.
    // Сказать «не выставлен» значило бы спровоцировать второй ордер на те же
    // деньги — то же правило, что в store/orders.ts и в исполнителе.
    return {
      text: `: судьба неизвестна — ${res.error || "ответ биржи не получен"}`,
      tone: "bad",
    };
  }
  if (isPostOnlyCross(res.error ?? "")) {
    // Post-only ордер, дотянувшийся до встречной заявки, гасится ПО
    // ОПРЕДЕЛЕНИЮ: ордера нет, денег не потрачено, позиция не изменилась. Тот
    // же штатный исход, что «цена ушла» у тейкера, и тем же серым: покрасив
    // его красным, мы приучили бы не читать красное.
    //
    // Текст отказа при этом СОХРАНЯЕТСЯ: гейт называет в нём встречную цену, и
    // она отвечает на единственный вопрос оператора — промахнулся он на цент
    // или стоит не на том рынке.
    return {
      text:
        ": стакан перекрыт — мейкером здесь не встать, деньги не потрачены" +
        (res.error ? ` (${res.error})` : ""),
      tone: "muted",
    };
  }
  if (isNoMatch(res.error ?? "")) {
    // Тейкер не нашёл встречной заявки: уровень ушёл, пока команда летела.
    // Это штатный исход FAK, а не поломка, — серым, и словами оператора.
    return { text: ": цена ушла — ордер погашен, деньги не потрачены", tone: "muted" };
  }
  return { text: `: отказ — ${res.error || "без объяснения"}`, tone: "bad" };
}

/**
 * Вердикты нескольких команд одного нажатия — одной строкой эха.
 *
 * Приняли все — так и сказано, с числом. Иначе — по аккаунту: «A01 → принят
 * биржей; A03: отказ — …», и тон худшего: зелёное над строкой, где один из
 * аккаунтов получил отказ, читалось бы как «закрыто».
 */
function legsEcho(awaiting: Awaiting, results: CommandResult[]): Echo {
  if (results.every((r) => r.accepted)) {
    return {
      text: `${awaiting.label} → ${awaiting.paper ? "проба налита по стакану" : "принят биржей"} (${results.length} из ${results.length})`,
      tone: "ok",
    };
  }
  const roster = useAccounts.getState().data;
  let tone: Echo["tone"] = "ok";
  const parts = results.map((res, i) => {
    const one = verdictEcho(res, awaiting.paper);
    if (one.tone === "bad") tone = "bad";
    else if (one.tone === "muted" && tone === "ok") tone = "muted";
    return `${accountLabel(roster, awaiting.legs[i]?.account)}${one.text}`;
  });
  return { text: `${awaiting.label}: ${parts.join("; ")}`, tone };
}

export function QuickActions({ market, tokens, className, pfSlug }: Props) {
  const settings = useSettings((s) => s.settings);
  const noticesFromSettings = useSettings((s) => s.notices);
  const seedFromTrader = useSettings((s) => s.seedFromTrader);
  const setActiveSize = useSettings((s) => s.setActiveSize);
  const setCustomSize = useSettings((s) => s.setCustomSize);
  const setTarget = useSettings((s) => s.setTarget);

  const armed = useHotkeys((s) => s.armed);
  const arm = useHotkeys((s) => s.arm);
  const disarm = useHotkeys((s) => s.disarm);

  const place = useOrders((s) => s.place);
  const placePaper = useOrders((s) => s.placePaper);
  const cancelAll = useOrders((s) => s.cancelAll);
  /**
   * Режим симуляции. Читается отсюда же, откуда его читает форма ордера: два
   * пути отправки обязаны видеть ОДНУ настройку, иначе «кнопки пробуют, форма
   * тратит» становится возможным состоянием экрана.
   */
  const paperMode = useSettings((s) => s.settings.paperMode);
  const orders = useOrders((s) => s.orders);
  const status = useStatus();

  // Строки панели: все аккаунты набора, сгруппированные по когортам.
  const roster = useAccounts((s) => s.data);
  const cohorts = useAccounts((s) => s.cohorts);
  const rosterError = useAccounts((s) => s.error);
  const rows = useMemo(() => quickRows(roster, cohorts, paperMode), [roster, cohorts, paperMode]);
  const accounts = useMemo(() => rowAccounts(rows), [rows]);

  // Снимки кошельков — у всех аккаунтов строк сразу.
  const positions = usePositionsOf(market.condition_id, accounts);
  // Ключ снимков — время и статус ответов: массив useQueries новый на каждом
  // рендере, и без ключа всё, что из него считается, пересобиралось бы с ним.
  const snapKey =
    accounts.join(",") +
    "|" +
    positions.queries.map((q) => `${q.dataUpdatedAt}:${q.errorUpdatedAt}:${q.status}`).join(",");
  const snaps = useMemo(
    () =>
      new Map<string, AccountSnap>(
        accounts.map((a, i) => {
          const q = positions.queries[i];
          return [a, { data: q?.data, problem: q ? snapProblem(q) : "позиция загружается…" }];
        }),
      ),
    [snapKey], // positions.queries — через ключ, см. snapKey
  );

  // НАШИ филлы по рынку — у каждого аккаунта свои: из них позиция видна сразу,
  // а снимок кошелька остаётся вторым мнением и основанием размера продажи.
  const fillsByMarket = useFills((s) => s.byMarket);
  const hydrateFills = useFills((s) => s.hydrate);
  useEffect(() => {
    void hydrateFills(market.condition_id);
  }, [hydrateFills, market.condition_id]);
  const marketFills = useMemo(
    () => fillsOfMarket(fillsByMarket, market.condition_id),
    [fillsByMarket, market.condition_id],
  );
  const filledBy = useMemo(
    () =>
      new Map(
        // Токены рынка нужны, чтобы отнести МЕЙКЕРСКИЙ филл к своему исходу: в
        // записи сделки лежит токен ТЕЙКЕР-ордера (lib/fills.ourAsset).
        accounts.map((a) => [a, positionsFromFills(scopeFills(marketFills, [a]), tokens)]),
      ),
    [marketFills, accounts, tokens],
  );

  const [echo, setEcho] = useState<Echo | null>(null);
  const [editing, setEditing] = useState(false);
  /**
   * Какой долей позиции закрываем. Состояние компонента, а НЕ настройка.
   *
   * Тот же довод, по которому в settings нет боевого режима: страница, которую
   * только что открыли, — это страница, на которой оператор ещё не
   * сориентировался. Умолчание 100 % видно на экране, а унаследованные с прошлой
   * сессии 25 % выглядели бы точно так же, как 100 %.
   */
  const [fraction, setFraction] = useState<SellFraction>(100);

  // Команды, вердикта которых ждёт эхо. Ключ — trace_id: у cancel_all нет
  // client_order_id вовсе. Команд бывает несколько — по одной на аккаунт
  // когорты или на ногу закрытия, — и эхо ждёт ВСЕ: «принят биржей» по ответу
  // первого было бы неправдой о втором.
  const [awaiting, setAwaiting] = useState<Awaiting | null>(null);
  const verdicts = useOrders(
    useShallow((s) => (awaiting ? awaiting.legs.map((l) => s.verdicts[l.traceId]) : NO_VERDICTS)),
  );

  /**
   * Заявка на кромке уже отправляется.
   *
   * Только у неё, и только на время запроса — не до вердикта биржи. Двойное
   * нажатие здесь стоит ВТОРОЙ стоящей заявки на полный пресет: у команд разные
   * client_order_id, идемпотентность их не схлопывает, и обе исполнятся.
   * Остальные кнопки панели не блокируются намеренно (см. resolveQuick): там
   * повтор — осознанный набор позиции по цене, которую оператор видит.
   */
  const [sendingMaker, setSendingMaker] = useState(false);

  const presign = status.data?.presign ?? null;
  const size = activeSizeOf(settings);
  const engaged = isEngaged(status.data);

  // Свежая вкладка засевается ПРЕСЕТАМИ трейдера, а не всем греемым набором:
  // ручная сумма из чужой сессии стала бы здесь кнопкой, которую оператор не
  // заводил, и осталась бы ею навсегда. Без засева первое же быстрое действие
  // промахнулось бы мимо кэша при полностью исправном кэше.
  useEffect(() => {
    if (presign) seedFromTrader(presign.presets_usd);
  }, [presign, seedFromTrader]);

  // Обратное направление: панель ГОВОРИТ трейдеру, что греть. Пресеты и последняя
  // ручная сумма — см. lib/warm.ts.
  const warm = useWarmSizes(presign);

  const referenceBook = useBooks((s) => (tokens[0] ? s.books[tokens[0].asset_id] : undefined));
  const tooSmall = useMemo(
    () => checkAgainstMarket(settings.sizes, market.min_order_size, referencePrice(referenceBook)),
    [settings.sizes, market.min_order_size, referenceBook],
  );
  // Каноническая ручная сумма — независимо от того, выбрана она сейчас или нет:
  // знак «~» отвечает за содержимое поля, а не за режим панели.
  const customUsd = useMemo(() => {
    const value = parse(settings.customSize);
    return value === null || value <= 0 ? null : format(value);
  }, [settings.customSize]);

  // Односторонне: набор трейдера ШИРЕ пресетов на ручную сумму, и равенства тут
  // быть не должно. Волнует обратное — сумма, которой можно нажать, не заготовлена.
  const cold = useMemo(
    () => coldSizes(warmRequest(settings).sizes_usd, presign?.sizes_usd),
    [settings, presign?.sizes_usd],
  );

  /** Почему позиция строки неизвестна; null — известна у всех её аккаунтов. */
  const unknownOf = useCallback(
    (row: QuickRow): string | null => {
      const many = row.accounts.length > 1;
      const parts: string[] = [];
      for (const a of row.accounts) {
        const p = snaps.get(a)?.problem ?? null;
        if (p) parts.push(many ? `${accountLabel(roster, a)}: ${p}` : p);
      }
      return parts.length > 0 ? `позиция неизвестна: ${parts.join("; ")}` : null;
    },
    [snaps, roster],
  );

  /**
   * План закрытия строки по исходу: по ноге на аккаунт строки.
   *
   * Ордера — аргументом: подпись кнопки считается по ордерам рендера, а само
   * нажатие — по живым (sendSell), и это последний рубеж обещания «продажа
   * никогда не превышает позицию».
   */
  const planFor = useCallback(
    (row: QuickRow, assetId: string, live: typeof orders): SellPlan =>
      sellPlan({
        holdings: row.accounts.map((account) => {
          const snap = snaps.get(account)?.data;
          const own = filledBy.get(account);
          return {
            account,
            indexed: indexedOf(snap, assetId),
            filled: ourShares(own, assetId),
            afterSale: journalAfterSale({
              fromChain: snap?.chain_known === true,
              filled: own ? filledPositionOf(own, assetId) : null,
              now: Date.now(),
            }),
          };
        }),
        orders: live,
        assetId,
        fraction,
        minOrderSize: market.min_order_size,
        label: (a) => accountLabel(useAccounts.getState().data, a),
        unknownReason: unknownOf(row),
      }),
    [snaps, filledBy, fraction, market.min_order_size, unknownOf],
  );

  /** Данные строк для таблицы: позиции по исходам, планы закрытия, ордера. */
  const rowData = useMemo(() => {
    const out = new Map<string, QuickRowData>();
    for (const row of rows) {
      if (row.kind === "group") continue;
      const own = positionsFromFills(scopeFills(marketFills, row.accounts), tokens);
      const merged = mergeSnapshots(row.accounts.map((a) => snaps.get(a)?.data));
      const cells = new Map<string, QuickCell>();
      for (const t of tokens) {
        const filled = filledPositionOf(own, t.asset_id);
        const snap = merged?.known ? positionOf(merged.positions, t.asset_id) : null;
        const snapShares = parse(snap?.size) ?? 0;
        // После нашей недавней продажи отставший снимок индексатора не
        // показывает проданное — то же правило, что у плана закрытия.
        const sold = journalAfterSale({
          fromChain: merged?.chain_known === true,
          filled,
          now: Date.now(),
        });
        const soldShares = sold === null ? null : (parse(sold) ?? 0);
        const afterSale = soldShares !== null && soldShares < snapShares ? soldShares : null;
        const fromFills = afterSale !== null || (filled !== null && filled.shares > 0);
        cells.set(t.asset_id, {
          plan: planFor(row, t.asset_id, orders),
          shares: afterSale ?? (fromFills && filled ? filled.shares : snapShares),
          avg: fromFills
            ? filled && filled.avgPrice > 0
              ? format(filled.avgPrice)
              : null
            : snapShares > 0
              ? (snap?.avg_price ?? null)
              : null,
          source: fromFills ? "fills" : snapShares > 0 ? "snapshot" : null,
          indexed: merged?.known ? (snap?.size ?? "0") : null,
          redeemable: snap?.redeemable === true,
        });
      }
      const want = new Set(row.accounts);
      const openOrders = Object.values(orders).filter(
        (o) =>
          o.condition_id === market.condition_id &&
          !isTerminal(o.status) &&
          o.account !== undefined &&
          want.has(o.account),
      ).length;
      out.set(row.key, { cells, unknown: unknownOf(row), openOrders });
    }
    return out;
  }, [rows, marketFills, tokens, snaps, planFor, orders, market.condition_id, unknownOf]);

  /**
   * Покупка от строки: аккаунт — один ордер, когорта — по ордеру полного
   * пресета на каждый годный аккаунт (web_api раскрывает её сам, Д-5).
   */
  const sendBuy = useCallback(
    async (
      row: QuickRow,
      action: QuickAction,
      assetId: string,
      usd: string,
      source: "quick" | "hotkey",
      clickedAtMono: number,
    ) => {
      const outcome = tokens.find((t) => t.asset_id === assetId)?.outcome ?? "";
      const head = `${row.label} · ${actionLabel(action, outcome)}`;
      if (!row.target || row.buyProblem !== null) {
        setEcho({ text: `${head}: ${row.buyProblem ?? "строка без цели"}`, tone: "bad" });
        return;
      }
      // Пере-резолвим из ТЕКУЩЕГО стакана в момент нажатия, а не из подписи
      // кнопки: подпись перерисовывается вслед за рынком, и оператор целится в
      // одну цену, а жмёт уже при другой. Эхо ниже скажет, что реально ушло.
      const resolved = resolveQuick(action, {
        market,
        assetId,
        book: useBooks.getState().books[assetId],
        usd,
      });
      if (!resolved.ok) {
        setEcho({ text: `${head}: ${resolved.reason}`, tone: "bad" });
        return;
      }
      // Мейкерская кнопка в симуляции не работает, и это НЕ «пока»: заявку
      // исполняет встречный поток, а не стакан, и проходом по книге её не
      // посчитать (ADR-0031). Отказ громкий — молчаливая подмена роли выдала бы
      // тейкерский налив за мейкерский вход.
      if (paperMode && action.mode === "join") {
        setEcho({
          text: "мейкерский вход не пробуется: его исполняет встречный поток, а не стакан",
          tone: "bad",
        });
        return;
      }
      // Комиссия дописывается к эху, когда она есть: эхо — это след действия в
      // журнале панели, и след без комиссии называл бы не ту сумму, которая ушла.
      const feeNote =
        resolved.fee !== null && (parse(resolved.fee) ?? 0) > 0
          ? ` + комиссия $${feeText(resolved.fee)} = $${resolved.usdNet}`
          : "";
      const each = row.kind === "cohort" ? ` с каждого (${row.note ?? ""})` : "";
      const label = `${head} ${resolved.price} × $${resolved.usd} (${resolved.size} share)${each}${feeNote}`;
      setEcho({ text: `${label} — отправляется…`, tone: "muted" });

      const meta = { source, clickedAtMono };
      const res = paperMode
        ? await placePaper(
            {
              condition_id: resolved.req.condition_id,
              asset_id: resolved.req.asset_id,
              side: resolved.req.side,
              price: resolved.req.price,
              size: resolved.req.size,
            },
            row.target,
            meta,
          )
        : await place(resolved.req, row.target, meta);
      if (!res.ok) {
        setEcho({ text: `${label}: ${res.error}`, tone: "bad" });
        return;
      }
      // 202 от web_api означает «команда принята в шину», и не более того.
      // Зелёным это быть НЕ ИМЕЕТ ПРАВА: биржа ещё не сказала ни слова. Ровно
      // так «отправлено» однажды и стояло зелёным над ордером, который биржа
      // отвергла по 401. Настоящий вердикт придёт по сокету — ждём его.
      setEcho({
        text:
          (paperMode
            ? `${label} → проба отправлена, считаем по стакану…`
            : `${label} → отправлено, ждём биржу…`) + partialNote(res.partial),
        tone: "muted",
      });
      setAwaiting({ label, legs: res.commands, paper: paperMode });
    },
    [market, place, placePaper, paperMode, tokens],
  );

  /**
   * Закрытие доли позиции строки — по ордеру на аккаунт, каждый от своего имени.
   *
   * Не командой когорты: та в режиме each продала бы с КАЖДОГО кошелька одно и
   * то же число share, которого на большинстве из них нет. Здесь у каждой ноги
   * своя цель `{account}` и свой размер — доля позиции этого кошелька. Цена у
   * ног общая: resolveQuick берёт уровень стакана, и от размера она не зависит.
   */
  const sendSell = useCallback(
    async (row: QuickRow, action: QuickAction, assetId: string, source: "quick" | "hotkey", clickedAtMono: number) => {
      const outcome = tokens.find((t) => t.asset_id === assetId)?.outcome ?? "";
      const head = `${row.label} · ${actionLabel(action, outcome)}`;
      const rosterNow = useAccounts.getState().data;
      const slot = (a: string) => accountLabel(rosterNow, a);
      if (row.sellProblem !== null) {
        setEcho({ text: `${head}: ${row.sellProblem}`, tone: "bad" });
        return;
      }

      const plan = planFor(row, assetId, useOrders.getState().orders);
      if (plan.legs.length === 0) {
        // Отката на долларовый размер нет ни при каких условиях: продать
        // «на $10» позицию, размер которой неизвестен, — это ордер, которого
        // никто не просил.
        setEcho({
          text: `${head}: ${plan.problem ?? `закрывать нечего — позиции по ${outcome} нет`}`,
          tone: "bad",
        });
        return;
      }
      // Та же причина, что у покупки: мейкерскую заявку исполняет встречный
      // поток, а не стакан, и пробой её не посчитать (ADR-0031).
      if (paperMode && action.mode === "join") {
        setEcho({
          text: "мейкерский выход не пробуется: его исполняет встречный поток, а не стакан",
          tone: "bad",
        });
        return;
      }

      const book = useBooks.getState().books[assetId];
      const legs: ResolvedLeg[] = [];
      for (const leg of plan.legs) {
        const r = resolveQuick(action, { market, assetId, book, shares: leg.shares });
        if (!r.ok) {
          const who = plan.legs.length > 1 ? `${slot(leg.account)}: ` : "";
          setEcho({ text: `${head}: ${who}${r.reason}`, tone: "bad" });
          return;
        }
        legs.push({ account: leg.account, r });
      }

      const label = sellLabel(head, legs, slot);
      setEcho({ text: `${label} — отправляется…`, tone: "muted" });

      const meta = { source, clickedAtMono };
      const sent = await Promise.all(
        legs.map(({ account, r }) =>
          paperMode
            ? placePaper(
                {
                  condition_id: r.req.condition_id,
                  asset_id: r.req.asset_id,
                  side: r.req.side,
                  price: r.req.price,
                  size: r.req.size,
                },
                { account },
                meta,
              )
            : place(r.req, { account }, meta),
        ),
      );
      const commands: Awaiting["legs"] = [];
      const failed: string[] = [];
      sent.forEach((res, i) => {
        const account = legs[i]?.account ?? "";
        if (res.ok) commands.push(...res.commands);
        else failed.push(legs.length > 1 ? `${slot(account)}: ${res.error}` : res.error);
      });
      if (commands.length === 0) {
        setEcho({ text: `${label}: ${failed.join("; ")}`, tone: "bad" });
        return;
      }
      // 202 — «команда принята в шину», и не более: зелёным быть не имеет права.
      setEcho({
        text:
          (paperMode
            ? `${label} → проба отправлена, считаем по стакану…`
            : `${label} → отправлено, ждём биржу…`) + partialNote(failed),
        tone: "muted",
      });
      setAwaiting({ label, legs: commands, paper: paperMode });
    },
    [market, place, placePaper, paperMode, planFor, tokens],
  );

  /**
   * Стоящая заявка по 0.99 на сторону, которая уже выиграла — от АКТИВНОЙ
   * строки.
   *
   * Своим путём, а не через sendBuy: у той цена берётся из стакана в момент
   * нажатия, а здесь она фиксирована, и сторона выбирается кодом. Общее у них —
   * эхо, ожидание вердикта по trace_id и правило «202 не зелёный».
   */
  const sendMaker = useCallback(
    async (clickedAtMono: number) => {
      // Та же причина, что у кнопки «Maker»: заявка стоит в стакане, а не
      // бьёт по нему, и симулировать её проходом по книге нельзя.
      if (paperMode) {
        setEcho({
          text: "стоящая заявка не пробуется: её исполняет встречный поток, а не стакан",
          tone: "bad",
        });
        return;
      }
      const resolved = resolveMaker({
        market,
        tokens,
        books: useBooks.getState().books,
        usd: activeSizeOf(useSettings.getState().settings),
      });
      if (!resolved.ok) {
        setEcho({ text: resolved.reason, tone: "bad" });
        return;
      }

      const command = commandTargetNow();
      const who = command.account
        ? accountLabel(useAccounts.getState().data, command.account)
        : (command.cohort ?? "");
      const label =
        `${who ? `${who} · ` : ""}Make ${resolved.price} на ${resolved.outcome.toUpperCase()}: ` +
        `$${resolved.usd} (${resolved.size} share)`;
      if (!command.target) {
        setEcho({ text: `${label}: активная строка не выбрана — ${command.problem}`, tone: "bad" });
        return;
      }
      const target = command.target;
      setEcho({ text: `${label} — отправляется…`, tone: "muted" });

      // Флаг ставится ДО запроса и снимается в finally: кнопка гаснет на всё
      // время полёта команды, включая любой её исход. Без этого второе нажатие
      // подряд ставит вторую заявку того же размера.
      setSendingMaker(true);
      let res;
      try {
        res = await place(resolved.req, target, { source: "quick", clickedAtMono });
      } finally {
        setSendingMaker(false);
      }
      if (!res.ok) {
        setEcho({ text: `${label}: ${res.error}`, tone: "bad" });
        return;
      }
      setEcho({
        text: `${label} → отправлено, ждём биржу…${partialNote(res.partial)}`,
        tone: "muted",
      });
      setAwaiting({ label, legs: res.commands });
    },
    [market, place, paperMode, tokens],
  );

  /**
   * Снятие ордеров СТРОКИ по этому рынку — аккаунта или когорты.
   *
   * Подтверждения нет, как и у «снять все»: снятие денег не тратит, а
   * возвращает, и жмут его ровно тогда, когда цена пошла не туда.
   */
  const cancelRow = useCallback(
    async (row: QuickRow, clickedAtMono: number) => {
      const label = `${row.label} · снять ордера по рынку`;
      if (!row.target || row.cancelProblem !== null) {
        setEcho({ text: `${label}: ${row.cancelProblem ?? "строка без цели"}`, tone: "bad" });
        return;
      }
      setEcho({ text: `${label} — отправляется…`, tone: "muted" });
      const res = await cancelAll(row.target, market.condition_id, { source: "quick", clickedAtMono });
      if (!res.ok) {
        setEcho({ text: `${label}: ${res.error}`, tone: "bad" });
        return;
      }
      setEcho({
        text: `${label} → отправлено, ждём биржу…${partialNote(res.partial)}`,
        tone: "muted",
      });
      setAwaiting({ label, legs: res.commands });
    },
    [cancelAll, market.condition_id],
  );

  /**
   * Массовое снятие по ЭТОМУ рынку у ВСЕХ аккаунтов — одним нажатием.
   *
   * Всех, а не активной строки (решение владельца 2026-10-07): это аварийный
   * тормоз, и снимать он обязан всё, что стоит, а не то, что выбрано. Снять у
   * одного — крестиком в его строке. Подтверждения нет: снятие денег не
   * тратит, а возвращает, и лишний шаг стоил бы тех самых секунд.
   */
  const cancelMarketOrders = useCallback(
    async (source: "quick" | "hotkey", clickedAtMono: number) => {
      const label = "снять все ордера по рынку · все аккаунты";
      setEcho({ text: `${label} — отправляется…`, tone: "muted" });
      const res = await cancelEverywhere(market.condition_id, { source, clickedAtMono });
      if (!res.ok) {
        setEcho({ text: `${label}: ${res.error}`, tone: "bad" });
        return;
      }
      // 202 — это «команда принята в шину», а не «ордера сняты»: настоящий
      // вердикт придёт по сокету, и до него зелёного здесь быть не может.
      setEcho({
        text: `${label} → отправлено, ждём биржу…${partialNote(res.partial)}`,
        tone: "muted",
      });
      setAwaiting({ label, legs: res.commands });
    },
    [market.condition_id],
  );

  // Вердикт по отправленным командам: эхо говорит, когда ответили ВСЕ команды
  // нажатия.
  //
  // Позиции здесь НЕ перечитываются, и это осознанное снятие. «Принят биржей» —
  // это постановка ордера, а не исполнение: у мейкера в этот момент ордер только
  // встал в стакан. Перечитывание висит на самом факте филла — событии trade и
  // выросшем size_matched (hooks/usePositions.ts).
  useEffect(() => {
    if (!awaiting || verdicts.length === 0) return;
    if (verdicts.some((v) => v === undefined)) return;
    const results = verdicts.map((v) => v!.res);
    if (results.length === 1) {
      const one = verdictEcho(results[0]!, awaiting.paper);
      setEcho({ text: `${awaiting.label}${one.text}`, tone: one.tone });
    } else {
      setEcho(legsEcho(awaiting, results));
    }
    setAwaiting(null);
  }, [awaiting, verdicts]);

  // Эхо гаснет само: залипший текст через минуту описывает уже не то действие.
  useEffect(() => {
    if (echo === null) return;
    const timer = setTimeout(() => setEcho(null), ECHO_MS);
    return () => clearTimeout(timer);
  }, [echo]);

  // Приёмник клавиш ставится страницей рынка и снимается при уходе.
  useEffect(() => {
    setHotkeySink((action: HotkeyAction) => {
      switch (action.type) {
        case "quick": {
          const state = useHotkeys.getState();
          if (!state.armed) {
            // НЕ молчим: молчаливое бездействие научило бы оператора, что
            // клавиша сломана, и обратное он узнал бы в худшую минуту.
            setEcho({ text: "горячие клавиши не в боевом режиме", tone: "muted" });
            return;
          }
          if (armExpired(state.lastActionAt, performance.now())) {
            state.disarm();
            setEcho({ text: "боевой режим погас сам после простоя", tone: "muted" });
            return;
          }
          const token = tokens[action.outcomeIndex];
          if (!token) {
            setEcho({ text: `у рынка нет исхода №${action.outcomeIndex + 1}`, tone: "muted" });
            return;
          }
          // Клавиши бьют от АКТИВНОЙ строки — выбор читается в момент нажатия:
          // клавиша не перерисовывает кнопку, и выбор рендера мог устареть.
          const row = rowOfChoice(rows, useSettings.getState().settings.target);
          if (!row) {
            setEcho({
              text: "клавиши бьют от активной строки — щёлкните по имени аккаунта или когорты",
              tone: "bad",
            });
            return;
          }
          const quick: QuickAction = { side: action.side, mode: action.mode };
          if (action.side === "SELL") {
            state.noteAction();
            void sendSell(row, quick, token.asset_id, "hotkey", performance.now());
            return;
          }
          if (size === null) {
            setEcho({ text: "не выбран размер", tone: "bad" });
            return;
          }
          state.noteAction();
          void sendBuy(row, quick, token.asset_id, size, "hotkey", performance.now());
          return;
        }
        case "size": {
          const s = useSettings.getState();
          if (action.index >= s.settings.sizes.length) {
            setEcho({ text: `пресета №${action.index + 1} нет`, tone: "muted" });
            return;
          }
          s.setActiveSize(action.index);
          return;
        }
        case "cancelMarket": {
          // Без боевого режима и без второго шага — по тому же доводу, что у
          // кнопки рядом: снятие ордеров денег не тратит, а возвращает.
          void cancelMarketOrders("hotkey", performance.now());
          return;
        }
        default:
          // «outcome» обслуживает форму ордера, а не панель.
          return;
      }
    });
    return () => setHotkeySink(null);
  }, [cancelMarketOrders, rows, sendBuy, sendSell, size, tokens]);

  /**
   * Боевой режим гаснет при смене РЫНКА, а не только при уходе со страницы.
   *
   * Переход по ссылке на соседнее окно серии остаётся ВНУТРИ маршрута
   * `market/:conditionID`: React меняет параметр и НЕ размонтирует страницу. С
   * эффектом на пустых зависимостях взведённый режим пережил бы такой переход,
   * и первое же нажатие на СЛЕДУЮЩЕМ рынке отправило бы настоящий ордер по
   * чужой вершине стакана.
   */
  useEffect(() => {
    return () => {
      useHotkeys.getState().disarm();
    };
  }, [market.condition_id]);

  // Гигиена при смене рынка: эхо прошлого окна к новому рынку отношения не
  // имеет, а выглядит как относящееся.
  useEffect(() => {
    setEcho(null);
    setAwaiting(null);
  }, [market.condition_id]);

  const activeRow = rowOfChoice(rows, settings.target);

  return (
    <Panel
      className={className}
      title="Быстрые действия"
      // Плотная карточка: панель стоит в ЛИПКОЙ полосе, и её отступы оплачены
      // не один раз при прокрутке, а всё время работы с рынком.
      dense
      /*
        Всё, что не является ставкой, живёт в ШАПКЕ панели: суммы, доля
        закрытия, «снять все», клавиши. Панель стоит в липкой полосе, и каждый
        её ряд оплачен всей высотой экрана.

        «Снять все» отделено от взвода клавиш подсказкой и замером задержки
        НАМЕРЕННО: соседство «включить оружие» и «снять всё» — это промах мышью
        в ту секунду, когда цена пошла не туда.
      */
      actions={
        <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-1">
          <SizeRow
            sizes={settings.sizes}
            active={settings.activeSize}
            mode={settings.sizeMode}
            custom={settings.customSize}
            customWarm={
              customUsd === null ? null : matchesPresign(customUsd, presign?.sizes_usd ?? null)
            }
            presign={presign?.sizes_usd ?? null}
            tooSmall={tooSmall}
            onPick={setActiveSize}
            onCustom={setCustomSize}
            onEdit={() => setEditing((v) => !v)}
          />
          {/* Доля закрытия — одна на все строки: решение «выходить ли целиком»
              не зависит от того, с какого кошелька. */}
          <span className="flex items-center gap-1">
            <span className="text-xs text-[var(--color-muted)]">Закрыть</span>
            {SELL_FRACTIONS.map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setFraction(f)}
                className="num rounded border px-1.5 py-0.5 text-xs"
                style={{
                  borderColor: f === fraction ? "var(--color-sell)" : "var(--color-border)",
                  color: f === fraction ? "var(--color-sell)" : "var(--color-muted)",
                }}
              >
                {f}%
              </button>
            ))}
          </span>
          {/* Кнопка НЕ блокируется стопом и не спрашивает подтверждения: снятие
              ордеров деньги возвращает, а не тратит. */}
          <button
            type="button"
            onClick={(e) => void cancelMarketOrders("quick", e.timeStamp || performance.now())}
            className="rounded border border-[var(--color-border)] px-2 py-1 text-xs text-[var(--color-muted)] hover:text-[var(--color-sell)]"
            title="снять ордера этого рынка у ВСЕХ аккаунтов набора; у одного — крестиком в его строке"
          >
            снять все
            <span className="kbd ml-1.5">⇧C</span>
          </button>
          <button
            type="button"
            onClick={() => useHotkeys.getState().toggleHelp(true)}
            className="kbd hover:text-[var(--color-fg)]"
            title="все горячие клавиши панели — и по клавише «?» из любого места"
          >
            ?
          </button>
          {/* Отдельным компонентом: числу нужен собственный тик часов, а держать
              его здесь запрещено правилом hooks/useNow.ts — эта панель тянет за
              собой перерисовку половины экрана. */}
          <LatencyBadge />
          <ArmToggle armed={armed} onArm={arm} onDisarm={disarm} />
        </div>
      }
    >
      <div className="flex flex-col gap-2">
        {settings.sizeMode === "custom" && size === null && (
          <p className="text-xs text-[var(--color-sell)]">
            ручная сумма «{settings.customSize}» не разобрана — кнопки покупки выключены
          </p>
        )}

        {editing && (
          <SizePresetsEditor
            sizes={settings.sizes}
            presign={presign?.sizes_usd ?? null}
            cold={cold}
            onClose={() => setEditing(false)}
          />
        )}

        {rows.length === 0 ? (
          <p className="text-xs text-amber-300">
            {roster && !roster.known
              ? `состав аккаунтов неизвестен: ${roster.reason ?? "нет sidecar'а и трейдер молчит"} — торговать не от кого`
              : rosterError
                ? `состав аккаунтов не прочитан: ${rosterError}`
                : roster
                  ? "в наборе нет аккаунтов"
                  : "состав аккаунтов загружается…"}
          </p>
        ) : (
          <QuickTable
            market={market}
            tokens={tokens}
            rows={rows}
            data={rowData}
            active={settings.target}
            usd={size}
            fraction={fraction}
            armed={armed}
            onBuy={(row, action, assetId, at) => {
              if (size !== null) void sendBuy(row, action, assetId, size, "quick", at);
            }}
            onSell={(row, action, assetId, at) => void sendSell(row, action, assetId, "quick", at)}
            onCancel={(row, at) => void cancelRow(row, at)}
            onActivate={(row) => setTarget(row.choice)}
            extra={
              pfSlug
                ? (detail, span) => (
                    // Своя граница: сбой строки predict.fun не вправе снять
                    // таблицу, с которой торгуют аккаунты Polymarket.
                    <ErrorBoundary
                      key={pfSlug}
                      fallback={(e) => (
                        <tr>
                          <td colSpan={span} className="border-t border-[var(--color-border)] py-1 text-xs text-amber-300">
                            строка PF упала: {e.message} — аккаунты Polymarket работают
                          </td>
                        </tr>
                      )}
                    >
                      <PFQuickRow
                        slug={pfSlug}
                        tokens={tokens}
                        detail={detail}
                        usd={size}
                        fraction={fraction}
                        onEcho={(text, tone) => setEcho({ text, tone })}
                      />
                    </ErrorBoundary>
                  )
                : undefined
            }
          />
        )}

        {/* Ни одной строки, способной хоть что-то сделать: трейдер не загрузил
            ни одного аккаунта (нет файла ключей, все выведены). Причина одна на
            всю панель, и сказать её надо одной строкой, а не догадкой по
            погашенным кнопкам. В симуляции пробы идут и так — погашено только
            снятие. */}
        {rows.length > 0 && rows.every((r) => r.kind === "group" || r.cancelProblem !== null) && (
          <p className="text-xs text-amber-300">
            {paperMode
              ? "трейдер не загрузил ни одного аккаунта набора — пробы работают, снятие погашено; "
              : "трейдер не загрузил ни одного аккаунта набора — кнопки погашены; "}
            проверьте acct_loaded на /healthz трейдера
          </p>
        )}

        {/* Без активной строки клавиши отказывают эхом; сказать это заранее —
            только когда клавиши взведены, иначе строка висела бы постоянно. */}
        {armed && rows.length > 0 && !activeRow && (
          <p className="text-xs text-amber-300">
            клавиши бьют от активной строки — щёлкните по имени аккаунта или когорты
          </p>
        )}

        {/* Заявка на кромке — ПОД таблицей: это тоже ставка, от активной строки. */}
        <MakerSection
          market={market}
          tokens={tokens}
          usd={size}
          sending={sendingMaker}
          onSend={sendMaker}
        />

        {/* Кнопки НЕ блокируются стопом: закон у трейдера, и его отказ оператор
            обязан увидеть — иначе интерфейс молча решает за него. */}
        {engaged && (
          <p className="text-xs text-amber-300">аварийный стоп включён — трейдер отклонит ордер</p>
        )}

        {/* Расхождение означает «трейдер ещё не догрел»: набор ему отправляет
            сама панель, и доезжает он за один обход лесенки. */}
        {cold.length > 0 && (
          <p className="text-xs text-amber-300">
            трейдер пока не греет: {cold.map((s) => `$${s}`).join(", ")} — помеченные «~»
            подписываются на лету и уходят медленнее
          </p>
        )}

        {warm.notices.map((message, i) => (
          <p key={i} className="text-xs text-[var(--color-sell)]">
            {message}
          </p>
        ))}

        {noticesFromSettings.map((n, i) => (
          <p
            key={i}
            className="text-xs"
            style={{ color: n.level === "warn" ? "var(--color-sell)" : "var(--color-muted)" }}
          >
            {n.message}
          </p>
        ))}

        {/* Слот эха ЗАРЕЗЕРВИРОВАН, а не появляется вместе с текстом: панель
            стоит в липкой полосе, и без резерва каждый ответ трейдера толкал бы
            вниз всю страницу — ровно в ту секунду, когда оператор целится в
            следующую кнопку. `-mt-2` гасит зазор стопки перед пустым слотом. */}
        <div className="-mt-2 min-h-4">
          {echo && (
            <p
              className="num text-xs leading-4"
              style={{
                color:
                  echo.tone === "ok"
                    ? "var(--color-buy)"
                    : echo.tone === "bad"
                      ? "var(--color-sell)"
                      : "var(--color-muted)",
              }}
            >
              {echo.text}
            </p>
          )}
        </div>
      </div>
    </Panel>
  );
}

/** Почему снимка аккаунта нет; null — есть и известен. */
function snapProblem(q: {
  isLoading: boolean;
  error: unknown;
  data: PositionsResponse | undefined;
}): string | null {
  if (q.isLoading) return "позиция загружается…";
  if (q.error) return (q.error as Error).message;
  if (q.data && !q.data.known) return "web_api не смог спросить кошелёк аккаунта";
  return null;
}

/**
 * Позиция по нашим филлам как строка. null — филлов не было или их нетто-ноль.
 */
function ourShares(
  filled: Map<string, FilledPosition> | undefined,
  assetId: string,
): string | null {
  if (!filled) return null;
  const p = filledPositionOf(filled, assetId);
  return p && p.shares > 0 ? format(p.shares) : null;
}

/** Нога закрытия, разрешённая в команду. */
type ResolvedLeg = { account: string; r: Extract<Resolved, { ok: true }> };

/**
 * Подпись закрытия для эха.
 *
 * Одна нога — прежняя строка: «A01 · Продать DOWN: Taker 0.42 × 10.61 share
 * (≈ $4.4562) + комиссия …». Несколько — с размером каждого аккаунта: оператор
 * сверяет эхо с тем, что нажал, и «20.41 share» без разбивки не сказало бы, с
 * каких кошельков они ушли.
 */
function sellLabel(head: string, legs: ResolvedLeg[], slot: (account: string) => string): string {
  const first = legs[0]!.r;
  let usd: Micro = 0;
  let net: Micro = 0;
  let fee: Micro | null = 0;
  let size: Micro = 0;
  for (const { r } of legs) {
    usd = add(usd, parse(r.usd) ?? 0) ?? usd;
    net = add(net, parse(r.usdNet) ?? 0) ?? net;
    size = add(size, parse(r.size) ?? 0) ?? size;
    const f = r.fee === null ? null : parse(r.fee);
    fee = fee === null || f === null ? null : (add(fee, f) ?? null);
  }
  const amounts =
    legs.length === 1
      ? `${first.size} share (≈ $${first.usd})`
      : `${legs.map((l) => `${slot(l.account)} ${l.r.size}`).join(" + ")} = ${format(size)} share (≈ $${format(usd)})`;
  const feeNote =
    fee !== null && fee > 0 ? ` + комиссия $${feeText(format(fee))} = $${format(net)}` : "";
  return `${head} ${first.price} × ${amounts}${feeNote}`;
}

/**
 * Заявка на кромке целиком: кнопка, авторежим и разворот над ними.
 *
 * # Почему свёрнута по умолчанию
 *
 * Потому что панель стоит в ЛИПКОЙ полосе, и каждый её ряд оплачен не
 * прокруткой, а всей высотой экрана, отнятой у накопления участников и ленты.
 * Момент, ради которого заявка сделана, наступает на секунды в конце окна, а
 * место она занимала все пять минут — включая те четыре, когда до кромки ещё
 * далеко и нажимать её нечем: до порога кнопка всё равно объясняет отказ
 * словами.
 *
 * # Что видно СВЁРНУТЫМ и почему это не косметика
 *
 * Взведённый авторежим. Свернуть переключатель, за которым трейдер САМ тратит
 * деньги, и не сказать об этом на полосе — значит спрятать открытое поручение:
 * оператор читал бы панель как «сегодня я ничего не ставил», пока по окну уходят
 * ордера. Поэтому ключ окна опрашивается и свёрнутым (тот же запрос, тот же
 * такт: хук делит кэш с рядом ниже), а строка разворота называет и сумму.
 *
 * # Почему состояние местное
 *
 * Переход на соседнее окно серии страницу не размонтирует (см. MarketPage), так
 * что разворот переживает смену рынка сам собой — стора для этого не нужно. А
 * перезагрузка сбрасывает его к свёрнутому, и это верное умолчание нового
 * сеанса: развернул — значит, сейчас этим торгуешь.
 */
function MakerSection({
  market,
  tokens,
  usd,
  sending,
  onSend,
}: {
  market: Market;
  tokens: Token[];
  usd: string | null;
  sending: boolean;
  onSend: (clickedAtMono: number) => void;
}) {
  const [open, setOpen] = useState(false);
  const arm = useAutoMaker(market.condition_id);
  const armed = arm.data?.enabled === true;

  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-wrap items-baseline gap-2">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="text-xs text-[var(--color-muted)] hover:text-white"
          title={
            `стоящая заявка на выигравшую сторону по ${MAKER_PRICE} — и поручение ` +
            "трейдеру ставить её самому на этом окне"
          }
          aria-expanded={open}
        >
          {open ? "▾" : "▸"} заявка на кромке по {MAKER_PRICE}
        </button>
        {/* Взведённое окно названо ЗДЕСЬ, а не только внутри: см. доккомментарий.
            Тем же цветом, что и сам переключатель во включённом положении. */}
        {armed && !open && (
          <span
            className="text-xs"
            style={{ color: "var(--color-sell)" }}
            title="трейдер поставит заявку сам, как только лидер дойдёт до порога. Развернуть — выключить"
          >
            ⚙ АВТО ВКЛ{arm.data?.usd ? ` · $${arm.data.usd}` : ""}
          </span>
        )}
      </div>

      {/* Ширину забирает кнопка — действие относится к рынку целиком, сторону
          выбирает не оператор, а стакан.

          Авторежим — В ТОЙ ЖЕ строке, справа, а не рядом под ней. Он не
          отдельное действие, а способ поручить трейдеру то самое, что кнопка
          делает по нажатию, и соседство это выражает не хуже вертикали — зато не
          стоит ряда в липкой полосе. На узком экране ряд честно переносится:
          min-w у кнопки не даёт сжать расчёт до нечитаемого. */}
      {open && (
        <div className="flex flex-wrap items-center gap-2">
          <div className="min-w-[22rem] flex-1">
            <MakerButton
              market={market}
              tokens={tokens}
              usd={usd}
              sending={sending}
              onSend={onSend}
            />
          </div>
          <AutoMakerRow market={market} />
        </div>
      )}
    </div>
  );
}

/**
 * Кнопка «Выставить make на 0.99».
 *
 * # Что она делает
 *
 * Ставит СТОЯЩУЮ заявку на покупку выигравшей стороны по фиксированной цене.
 * Смысл — в моменте, когда окно уже разрешено по существу, но ещё не
 * рассчитано: выигравшая сторона стоит 0.98–0.99, проигравшая 0.01–0.02, и тот,
 * кто набирает проигравшую по 0.01, ищет ровно нашу заявку (биржа сводит две
 * покупки дополняющих исходов, когда сумма цен достигает единицы). Мы при этом
 * мейкер: комиссии нет, а до погашения в 1.00 остаётся цент.
 *
 * # Почему отдельным компонентом
 *
 * Ради подписки на стаканы. `useBooks((s) => s.books[assetId])` внутри следит
 * РОВНО за своими двумя токенами; подписка на всю карту книг в родителе
 * перерисовывала бы липкую панель на каждую дельту любого рынка вкладки. Тот же
 * приём, что у OutcomeRow.
 *
 * # Чего у неё намеренно нет
 *
 * Горячей клавиши. Клавиша — единственное место в системе, где ошибку оператора
 * не поймает риск-гейт, и там он хотя бы выбирает сторону сам. Здесь сторону и
 * цену выбирает код, так что сверить глазами перед нажатием нечего, и вешать на
 * это клавишу значило бы отдать решение целиком автоматике.
 *
 * Выключения по перекрытому стакану — тоже нет. Кнопка гаснет, только когда
 * подставить нечего; перекрытие она называет словами и отправляет команду.
 * Запрет — дело трейдера и биржи, и их мотивированный отказ оператор обязан
 * увидеть (то же правило, по которому кнопки не блокируются аварийным стопом).
 */
function MakerButton({
  market,
  tokens,
  usd,
  sending,
  onSend,
}: {
  market: Market;
  tokens: Token[];
  /** Активная сумма панели; null — не выбрана или не разобрана. */
  usd: string | null;
  /**
   * Команда этой кнопки уже в полёте.
   *
   * Кнопка на время отправки гаснет, и это единственное место панели, где
   * блокировка оправдана: у остальных кнопок сторону и цену выбирает оператор,
   * а здесь второе нажатие подряд ставит ВТОРУЮ заявку того же размера — на
   * бирже это две стоящие заявки на пресет каждая, и обе исполнятся.
   */
  sending: boolean;
  onSend: (clickedAtMono: number) => void;
}) {
  // Два токена поимённо, а не цикл: хуков в цикле переменной длины не бывает, а
  // рынок этой серии бинарный по построению.
  const first = tokens[0]?.asset_id ?? "";
  const second = tokens[1]?.asset_id ?? "";
  const bookA = useBooks((s) => s.books[first]);
  const bookB = useBooks((s) => s.books[second]);

  const resolved = useMemo((): ReturnType<typeof resolveMaker> => {
    // Небинарный рынок отсекается ЗДЕСЬ и своими словами. Отдать его
    // resolveMaker было бы враньём: книг третьего исхода этот компонент не
    // запрашивал, и причиной оказалось бы «стакан ещё не запрошен» — оператор
    // пошёл бы чинить подписку вместо того, чтобы понять, что кнопка про
    // двусторонний рынок.
    if (tokens.length !== 2) {
      return {
        ok: false,
        reason: `у рынка ${tokens.length} исходов — заявка на кромке рассчитана на двусторонний`,
      };
    }
    return resolveMaker({ market, tokens, books: { [first]: bookA, [second]: bookB }, usd });
  }, [market, tokens, first, second, bookA, bookB, usd]);

  // Без цели кнопка гаснет заранее, а не отказывает по нажатию: отказ в эхо
  // замечают уже после клика, а погашенная кнопка видна до него.
  const hasTarget = useHasCommandTarget();
  const blocked = !resolved.ok || sending || !hasTarget;
  // Цвет исхода тот же, что у его ряда выше: кнопка ставит на конкретную
  // сторону, и опознаваться она обязана тем же признаком, что и ряд. Пока
  // сторона не выбрана, цвет нейтральный — красить нечего.
  const row = resolved.ok ? tokens.findIndex((t) => t.asset_id === resolved.req.asset_id) : -1;
  const color = row < 0 ? "var(--color-muted)" : outcomeColor(row);
  // Перекрытие — одно понятие о двух сторонах стакана: своя встречная продажа
  // и чужая покупка дополняющего исхода. Оператору важен один вывод — заявка
  // не встанет, — поэтому и предупреждение одно, но цену называет ту, которая
  // мешает.
  const counter = resolved.ok ? (resolved.crossing ?? resolved.mint) : null;

  return (
    <button
      type="button"
      onClick={(e) => !blocked && onSend(e.timeStamp || performance.now())}
      disabled={blocked}
      // В ОДНУ строку, а не в две: кнопка идёт во всю ширину панели (на рабочем
      // экране это больше тысячи пикселей), и второй строке там нечего делать —
      // она стоила ряда в липкой полосе, ничего не добавляя. flex-wrap оставлен:
      // на узком экране строка честно переносится, а не режет расчёт.
      className="flex w-full flex-wrap items-baseline gap-x-2 gap-y-0.5 rounded border px-2.5 py-1.5 text-left disabled:opacity-40"
      style={{
        borderColor: color,
        color,
        ...(blocked ? {} : { backgroundColor: `color-mix(in srgb, ${color} 14%, transparent)` }),
      }}
      title={
        !resolved.ok
          ? resolved.reason
          : `стоящая заявка: купить ${resolved.outcome} по ${resolved.price} × ` +
            `${resolved.size} share ($${resolved.usd}). Ордер помечен post_only — биржа ` +
            `отвергнет его целиком, если он перекроет стакан, вместо того чтобы ` +
            `исполнить тейкером` +
            (resolved.crossing === null
              ? ""
              : `. Сейчас встречная продажа стоит ${resolved.crossing}, то есть ордер, ` +
                `скорее всего, будет отвергнут`) +
            (resolved.mint === null
              ? ""
              : `. В стакане дополняющего исхода стоит покупка по ${resolved.mint}: сумма ` +
                `цен достигает единицы, биржа свела бы их типом MINT — и отвергнет нашу ` +
                `заявку, потому что тейкером в этой паре оказываемся мы`) +
            (sending ? ". Команда уже отправлена — ждём ответа" : "")
      }
    >
      <span className="shrink-0 text-xs font-medium">
        Make {MAKER_PRICE}
        {resolved.ok && ` на ${resolved.outcome.toUpperCase()}`}
      </span>
      <span className="num min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-xs text-[var(--color-muted)]">
        {!resolved.ok
          ? resolved.reason
          : `${resolved.price} × $${resolved.usd} (${resolved.size} share)`}
      </span>
      {/* Предупреждение о перекрытии — янтарным и словами. Это не отказ: ордер
          уйдёт, а гасит его биржа, и оператор обязан понимать заранее, чем
          нажатие кончится. */}
      {counter !== null && (
        <span
          className="shrink-0 text-xs text-amber-300"
          title={
            resolved.ok && resolved.crossing === null
              ? "в стакане дополняющего исхода стоит покупка, с которой биржа свела бы " +
                "нашу заявку типом MINT: тейкером в этой паре оказываемся мы, и post-only " +
                "ордер биржа отвергает целиком"
              : "post-only ордер, дотянувшийся до встречной заявки, биржа отвергает целиком"
          }
        >
          встречная {counter} — не встанет
        </span>
      )}
      {/* Роль названа на самой кнопке: она и есть всё содержание действия, а
          отличить её от соседней «Maker L1» по одной цене нельзя. */}
      <span className="ml-auto shrink-0 text-xs text-[var(--color-muted)]">
        {resolved.ok && resolved.fee === "0" ? "мейкер, без комиссии" : "мейкер"}
      </span>
    </button>
  );
}

/**
 * Авторежим заявки на кромке: поручить трейдеру нажимать кнопку выше.
 *
 * # Зачем он вообще нужен
 *
 * Момент, ради которого кнопка сделана, наступает на секунды и не всегда в тот
 * час, когда оператор смотрит на экран. Трейдер видит стакан тик в тик и в
 * браузере не нуждается.
 *
 * # Почему разрешение выдаётся ПО ОДНОМУ ОКНУ
 *
 * Потому что окна живут пять минут и сменяются сами. Один тумблер на всё означал
 * бы решение, принятое однажды и действующее на рынках, которых в тот момент ещё
 * не существовало. Ключ на той стороне ключуется рынком: у нового окна его
 * просто нет, и каждое окно начинается ВЫКЛЮЧЕННЫМ. Оператор включает режим,
 * глядя на этот конкретный стакан.
 *
 * # Почему сумма не подставляется
 *
 * Пустое поле — это отказ включаться, а не «возьмём пресет панели». Сумма и флаг
 * — одно решение: подставь мы сюда активный пресет, и закрытая вкладка или
 * случайный клик по цифре меняли бы то, чем торгует трейдер, пока оператора нет
 * рядом. Ровно этого авторежим и не должен уметь.
 *
 * # Чего у режима нет
 *
 * Горячей клавиши. Как и у кнопки выше, и по той же причине: сторону и цену
 * выбирает код, сверять глазами перед нажатием нечего. Включение автоматической
 * траты денег обязано стоить движения мышью.
 */
function AutoMakerRow({ market }: { market: Market }) {
  const conditionID = market.condition_id;
  const arm = useAutoMaker(conditionID);
  const save = useSetAutoMaker(conditionID);
  const status = useStatus();

  // Черновик суммы — СВОЙ на окно: разрешение не переезжает между окнами, и
  // сумма, набранная на прошлой пятиминутке, не должна оказаться взведённой на
  // следующей одним нажатием.
  const [draft, setDraft] = useState("");
  useEffect(() => setDraft(""), [conditionID]);

  const enabled = arm.data?.enabled === true;
  // Пока оператор ничего не набрал, показывается сумма, которая уже записана:
  // взведённое окно обязано показывать то, чем торгует, а не пустое поле.
  const usd = draft !== "" ? draft : enabled ? (arm.data?.usd ?? "") : "";
  const amount = parse(usd);

  // Потолок окружения приходит от ТРЕЙДЕРА, а не подразумевается здесь: web_api
  // про AUTOMAKER_ENABLED не знает by design, и переключатель, обещающий то,
  // чего трейдер не сделает, — худшее, что здесь может быть.
  const note = status.data?.automaker ?? "";
  const ceiling = status.data?.automaker_max_usd ?? "";
  const bid = status.data?.automaker_bid ?? "";
  const off = note !== "" && note !== "включён";
  // Три состояния, а не два. «Статус ещё не пришёл» — это первая секунда после
  // открытия вкладки, и объявлять там трейдера мёртвым значило бы пугать
  // оператора на каждом заходе. Ноту трейдер отдаёт всегда, в том числе
  // выключенным, поэтому её отсутствие ПРИ ПРИШЕДШЕМ статусе — настоящий отказ.
  const loading = status.data === undefined;
  const unknown = !loading && note === "";

  const problem = loading
    ? "статус ещё не пришёл"
    : unknown
      ? "трейдер не отвечает — включать нечего"
      : off
        ? `авторежим выключен у трейдера: ${note}`
        : enabled
          ? null
          : amount === null || amount <= 0
            ? "наберите сумму: без неё трейдер не поставит ни одного ордера"
            : null;

  const toggle = () => {
    if (enabled) {
      save.mutate({ enabled: false });
      return;
    }
    if (problem !== null) return;
    save.mutate({ enabled: true, usd });
    setDraft("");
  };

  return (
    /* Два ЯРУСА, а не одна переносящаяся строка: сверху то, что нажимают
       (переключатель и сумма), снизу то, что читают (числа трейдера и причина
       отказа). Одной строкой блок расползался в ширину и переносился посреди
       себя — рядом с заявкой на кромке, где ширина как раз дорога; двумя
       короткими ярусами он занимает столько же по высоте, но перестаёт толкать
       соседа. */
    <div className="flex flex-col gap-0.5 text-xs">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={toggle}
          disabled={save.isPending || (!enabled && problem !== null)}
          className="rounded border px-2 py-1 font-medium disabled:opacity-40"
          style={{
            borderColor: enabled ? "var(--color-sell)" : "var(--color-border)",
            color: enabled ? "var(--color-sell)" : "var(--color-muted)",
          }}
          title={
            enabled
              ? `трейдер сам поставит заявку по ${MAKER_PRICE} на $${arm.data?.usd ?? ""}, ` +
                `как только лидер дойдёт до ${bid || "порога"}. Нажать — выключить`
              : (problem ??
                `поручить трейдеру ставить заявку по ${MAKER_PRICE} на этом окне, ` +
                  `когда лидер дойдёт до ${bid || "порога"}`)
          }
        >
          ⚙ {enabled ? "АВТО ВКЛ" : "авто выкл"}
        </button>

        {/* Поле рядом с переключателем, а не в панели пресетов: это сумма ОДНОГО
            решения по ОДНОМУ окну, и путать её с тем, чем оператор бьёт руками,
            нельзя. */}
        <label className="flex items-center gap-1 text-[var(--color-muted)]">
          $
          <input
            value={usd}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !enabled && toggle()}
            disabled={enabled || off || unknown || loading}
            inputMode="decimal"
            placeholder="сумма"
            className="num w-16 rounded border border-[var(--color-border)] bg-transparent px-1 py-0.5 text-right text-[var(--color-fg)] disabled:opacity-60"
            title={
              enabled
                ? "сумма взведённого окна не правится на ходу: выключите режим, чтобы поменять"
                : "сумма ОДНОЙ заявки авторежима на этом окне"
            }
          />
        </label>
      </div>

      {/* Нижний ярус — только чтение. Порог и потолок это числа ТРЕЙДЕРА, а не
          наши: он по ним и торгует. Стоят рядом, потому что первый отвечает
          «когда сработает», второй — «сколько он всё равно не превысит».
          Причина отказа тут же: она объясняет погашенный переключатель прямо
          под ним. */}
      <div className="flex flex-wrap items-baseline gap-x-2 text-[var(--color-muted)]">
        <span>
          {bid !== "" && `порог ${bid}`}
          {ceiling !== "" && ` · не больше $${ceiling}`}
        </span>

        {/* Отказ записи — словами: молча не сработавший переключатель означает,
            что оператор считает окно взведённым, а оно не взведено. */}
        {save.isError && (
          <span className="text-[var(--color-sell)]">
            не записано: {(save.error as Error).message}
          </span>
        )}
        {problem !== null && !enabled && !save.isError && (
          <span className={off || unknown ? "text-amber-300" : undefined}>{problem}</span>
        )}
      </div>

      {/* Аккаунт авторежима один на все окна (automaker:account), а не на
          рынок: показан здесь, потому что «АВТО ВКЛ» без него не торгует. */}
      <SubsystemAccountSelect subsystem="automaker" />
    </div>
  );
}

/**
 * Переключатель боевого режима.
 *
 * Включается ТОЛЬКО мышью и не имеет клавиатурного пути — асимметрия та же, что
 * у аварийного стопа, но повёрнутая в опасную сторону: включить трудно (нужна
 * мышь), выключить легко (Esc). Хоткей — единственное место в системе, где
 * ошибку оператора не поймает риск-гейт: ордер размером с пресет по цене
 * вершины стакана он обязан ОДОБРИТЬ.
 */
function ArmToggle({
  armed,
  onArm,
  onDisarm,
}: {
  armed: boolean;
  onArm: () => void;
  onDisarm: () => void;
}) {
  return (
    <button
      type="button"
      onClick={() => (armed ? onDisarm() : onArm())}
      className="rounded border px-2 py-1 text-xs font-medium"
      style={{
        borderColor: armed ? "var(--color-sell)" : "var(--color-border)",
        color: armed ? "var(--color-sell)" : "var(--color-muted)",
      }}
      title={
        armed
          ? "клавиши отправляют настоящие ордера; Esc — выключить"
          : "включить отправку ордеров с клавиатуры"
      }
    >
      ⌨ {armed ? "БОЕВОЙ РЕЖИМ" : "клавиши выключены"}
    </button>
  );
}

/** Пресеты суммы и поле ручного ввода. */
function SizeRow({
  sizes,
  active,
  mode,
  custom,
  customWarm,
  presign,
  tooSmall,
  onPick,
  onCustom,
  onEdit,
}: {
  sizes: string[];
  active: number;
  mode: "preset" | "custom";
  custom: string;
  /** Греет ли трейдер набранную сумму; null — набранного нечем разобрать. */
  customWarm: "yes" | "no" | "unknown" | null;
  presign: string[] | null;
  tooSmall: Map<string, string>;
  onPick: (i: number) => void;
  onCustom: (raw: string) => void;
  onEdit: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="text-xs text-[var(--color-muted)]">Сумма, $</span>
      {sizes.map((size, i) => {
        const warm = matchesPresign(size, presign);
        const small = tooSmall.get(size);
        const chosen = mode === "preset" && i === active;
        return (
          <button
            key={size}
            type="button"
            onClick={() => onPick(i)}
            disabled={small !== undefined}
            title={
              small ??
              (warm === "yes"
                ? "трейдер держит эту сумму пред-подписанной"
                : warm === "no"
                  ? "трейдер ещё не греет эту сумму — ордер подпишется на лету, задержка выше; " +
                    "набор доезжает до него за секунду после правки"
                  : "трейдер не сообщает список пред-подписанных сумм")
            }
            className="num rounded border px-2 py-1 text-xs disabled:opacity-40"
            style={{
              borderColor: chosen ? "var(--color-accent)" : "var(--color-border)",
              color: chosen ? "inherit" : "var(--color-muted)",
            }}
          >
            {size}
            {warm === "no" && <span className="ml-0.5 text-amber-300">~</span>}
            {i < 9 && <span className="kbd ml-1.5">{i + 1}</span>}
          </button>
        );
      })}

      {/* Своя сумма. С вехи «набор задаёт панель» она ГРЕЕТСЯ так же, как пресет
          (lib/warm.ts), поэтому знак «~» здесь больше не постоянный: он держится
          ровно до того обхода лесенки, на котором трейдер её заготовит. Остаётся
          навсегда — только если греть её трейдер отказался, и тогда причину
          называют замечания под панелью. */}
      <input
        value={custom}
        onChange={(e) => onCustom(e.target.value)}
        onFocus={() => custom.trim() !== "" && onCustom(custom)}
        inputMode="decimal"
        placeholder="своя"
        title={
          "сумма сохраняется в браузере, подставляется при следующем открытии и " +
          "уходит трейдеру на прогрев; пока курсор в поле, горячие клавиши ордеров " +
          "не отправляют (Esc — выйти)"
        }
        className="num w-20 rounded border bg-transparent px-2 py-1 text-xs outline-none placeholder:text-[var(--color-muted)]"
        style={{
          borderColor: mode === "custom" ? "var(--color-accent)" : "var(--color-border)",
          color: mode === "custom" ? "inherit" : "var(--color-muted)",
        }}
      />
      {mode === "custom" && customWarm === "no" && (
        <span
          className="text-xs text-amber-300"
          title="трейдер ещё не греет эту сумму — ордер подпишется на лету, задержка выше"
        >
          ~
        </span>
      )}

      <button
        type="button"
        onClick={onEdit}
        className="rounded border border-[var(--color-border)] px-2 py-1 text-xs text-[var(--color-muted)] hover:text-white"
      >
        настроить
      </button>
    </div>
  );
}

/**
 * Комиссия для показа: центы, но НЕ в ноль.
 *
 * Центами её показывать нельзя безоговорочно: на краях цены комиссия честно бывает
 * меньше цента — 0.00699 на пятиконечной покупке по 0.98, — а `cents` усекает, и на
 * кнопке появилось бы «−$0.00». Это хуже, чем отсутствие числа: ноль читается как
 * «комиссии нет» ровно там, где она есть, то есть возвращает дефект, который веха
 * M7 и закрывает. Мелкая комиссия показывается четырьмя знаками.
 */
function feeText(usd: string): string {
  const m = parse(usd);
  if (m === null) return usd;
  return m < 10_000 ? toFixed(m, 4) : toFixed(m, 2);
}
